import JSZip from 'jszip'
import { ACCEPTABLE_FILE_TYPES } from '../../Configuration'
import {
  getFilesForDetail,
  uploadDetailFiles,
  deleteDetailFile,
  deleteFolderFromCaseBlobs,
  getDetailFileContent,
  createFolderInCaseBlobs,
} from '../../services/detailview'

export const getFileExtension = (file: File): string => {
  const fileName = file.name
  const ext = fileName.split('.').pop()
  return ext?.toLowerCase() || ''
}

export const getFileExtensionByFileName = (fileName: string): string => {
  const ext = fileName.split('.').pop()
  return ext?.toLowerCase() || ''
}
export const isAcceptableFileType = (fileName: string) => {
  const data = ACCEPTABLE_FILE_TYPES?.includes(getFileExtensionByFileName(fileName))
  return data
}
export const trimBeforeExtension = (fileName: string) => {
  const parts = fileName.split('.')
  const extension = parts.pop()
  const nameWithoutExtension = parts.join('.').trim()
  return `${nameWithoutExtension}.${extension}`
}
export const compareArraysOfFiles = (initialFiles: Array<File>, newFiles: Array<File>): [Array<File>, Array<File>] => {
  const removedFiles: File[] = []
  const newAddedFiles: File[] = []

  initialFiles.forEach((initialFile) => {
    const found = newFiles.some((newFile) => {
      return (
        newFile.name === initialFile.name &&
                newFile.size === initialFile.size &&
                newFile.type === initialFile.type
      )
    })

    if (!found) {
      removedFiles.push(initialFile)
    }
  })

  newFiles.forEach((newFile) => {
    const found = initialFiles.some((initialFile) => {
      return (
        newFile.name === initialFile.name &&
                newFile.size === initialFile.size &&
                newFile.type === initialFile.type
      )
    })

    if (!found) {
      newAddedFiles.push(newFile)
    }
  })

  return [removedFiles, newAddedFiles]
}

export const downloadFile = (file: File) => {
  const link = document.createElement('a')
  link.href = window.URL.createObjectURL(file)
  link.download = file.name
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const getContentType = (file: File): string => {
  return file.type
}

export const loadDetailFiles = async (
  itemKey: string,
  setFiles: (files: any[]) => void,
  setFolders: (folders: any[]) => void,
  setIsFilesLoading: (v: boolean) => void,
  setFileError: (err: string | null) => void,
  pageName?: string
): Promise<void> => {
  if (!itemKey) {
    setFiles([])
    setFolders([])
    return
  }
  setIsFilesLoading(true)
  setFileError(null)
  try {
    const fileStructure = await getFilesForDetail(itemKey, pageName)
    setFiles(fileStructure.files || [])
    setFolders(fileStructure.accordionItems || [])
  } catch (error) {
    console.error('[loadDetailFiles]', error)
    setFileError('Unable to load files. Please try again.')
    setFiles([])
    setFolders([])
  } finally {
    setIsFilesLoading(false)
  }
}

export const handleDetailUploadFiles = async (
  uploadedFiles: File[],
  itemKey: string,
  setIsFilesLoading: (v: boolean) => void,
  setFileError: (err: string | null) => void,
  loadFiles: () => Promise<void>,
  folderName?: string,
  me?: any,
  pageName?: string,
  existingETagMap?: Map<string, string>
): Promise<boolean> => {
  if (!itemKey) {
    setFileError('Unable to upload files because the case item key was not found.')
    return false
  }
  setIsFilesLoading(true)
  setFileError(null)
  let uploadSucceeded = false
  try {
    try {
      await uploadDetailFiles(itemKey, uploadedFiles, folderName, me, pageName, existingETagMap)
      uploadSucceeded = true
    } catch (error) {
      console.error('[handleDetailUploadFiles]', error)
      setFileError('Unable to upload files. Please try again.')
    }
    // Always refresh the file list — the file may have been stored even if the API
    // response was an error. loadDetailFiles clears fileError at its own start, so
    // a successful load also dismisses any upload-error banner and closes the popup.
    await loadFiles()
  } finally {
    setIsFilesLoading(false)
  }
  return uploadSucceeded
}

export const handleDetailDeleteFiles = async (
  selectedFiles: Array<{ Name: string; folderName?: string }>,
  itemKey: string,
  setIsFilesLoading: (v: boolean) => void,
  setFileError: (err: string | null) => void,
  loadFiles: () => Promise<void>,
  folderName?: string,
  folderTitles?: string[],
  pageName?: string
): Promise<void> => {
  if (!itemKey) {
    setFileError('Unable to delete files because the case item key was not found.')
    return
  }
  const filesArray = Array.isArray(selectedFiles) ? selectedFiles : (selectedFiles ? [selectedFiles] : [])
  const folderSet = new Set(folderTitles ?? [])
  const foldersToDelete = filesArray.filter((f) => folderSet.has(f.Name))
  const filesToDelete = filesArray.filter((f) => !folderSet.has(f.Name))
  setIsFilesLoading(true)
  setFileError(null)
  try {
    try {
      await Promise.all([
        ...foldersToDelete.map((folder) => deleteFolderFromCaseBlobs(itemKey, folder.Name, pageName)),
        ...filesToDelete.map((file) => {
          const fileWithFolder = folderName !== undefined ? { ...file, folderName } : file
          return deleteDetailFile(itemKey, fileWithFolder, pageName)
        }),
      ])
    } catch (error) {
      console.error('[handleDetailDeleteFiles]', error)
      setFileError('Unable to delete files. Please try again.')
    }
    await loadFiles()
  } finally {
    setIsFilesLoading(false)
  }
}

export const triggerDetailDownload = async (
  file: { Name?: string; ServerRelativeUrl?: string; metadata?: any },
  itemKey: string,
  pageName?: string
): Promise<void> => {
  if (!file?.Name) return
  if (!itemKey && !file.ServerRelativeUrl) {
    console.error('[triggerDetailDownload] No itemKey or ServerRelativeUrl for file:', file.Name)
    return
  }
  const link = document.createElement('a')
  let objectUrl = ''
  try {
    if (file.ServerRelativeUrl) {
      link.href = file.ServerRelativeUrl
    } else {
      const content = await getDetailFileContent(itemKey, file, pageName)
      const type = file?.metadata?.contentType || 'application/octet-stream'
      objectUrl = window.URL.createObjectURL(new Blob([content], { type }))
      link.href = objectUrl
    }
    link.download = file.Name
    document.body.appendChild(link)
    link.click()
  } catch (error) {
    console.error('[triggerDetailDownload]', error)
  } finally {
    link.remove()
    if (objectUrl) window.URL.revokeObjectURL(objectUrl)
  }
}

export const handleDetailDownloadFile = (
  file: any,
  itemKey: string,
  pageName?: string
): void => {
  if (Array.isArray(file)) {
    file.forEach((item) => void triggerDetailDownload(item, itemKey, pageName))
    return
  }
  void triggerDetailDownload(file, itemKey, pageName)
}

export const handleDetailDownloadAllFiles = async (
  filesToDownload: any[],
  itemKey: string,
  pageName?: string
): Promise<void> => {
  if (!filesToDownload.length) return
  const zip = new JSZip()
  const results = await Promise.allSettled(
    filesToDownload.map(async (file) => {
      if (!file?.Name) return
      const content = file.ServerRelativeUrl
        ? await fetch(file.ServerRelativeUrl).then((r) => r.arrayBuffer())
        : await getDetailFileContent(itemKey, file, pageName)
      zip.file(file.Name, content)
    })
  )
  const failed = results.filter((r) => r.status === 'rejected')
  if (failed.length) {
    console.error('[handleDetailDownloadAllFiles] Some files failed to fetch:', failed)
  }
  if (results.every((r) => r.status === 'rejected')) return
  const blob = await zip.generateAsync({ type: 'blob' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'files.zip'
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const handleDetailDeleteSingleFile = async (
  fileToDelete: any,
  itemKey: string,
  setIsFilesLoading: (v: boolean) => void,
  setFileError: (err: string | null) => void,
  loadFiles: () => Promise<void>,
  pageName?: string
): Promise<void> => {
  if (!fileToDelete?.Name || !itemKey) return
  setIsFilesLoading(true)
  setFileError(null)
  try {
    try {
      await deleteDetailFile(itemKey, fileToDelete, pageName)
    } catch (error) {
      console.error('[handleDetailDeleteSingleFile]', error)
      setFileError('Unable to delete file. Please try again.')
    }
    // Always refresh so the list reflects backend state even when the API
    // response is an error. loadDetailFiles clears fileError at its own start,
    // which also prevents the Add Files popup from reopening on false-positive errors.
    await loadFiles()
  } finally {
    setIsFilesLoading(false)
  }
}

export const handleDetailAddFolder = async (
  folderName: string,
  itemKey: string,
  setIsFilesLoading: (v: boolean) => void,
  setFileError: (err: string | null) => void,
  loadFiles: () => Promise<void>,
  pageName?: string
): Promise<void> => {
  if (!itemKey || !folderName?.trim()) return
  setIsFilesLoading(true)
  setFileError(null)
  try {
    await createFolderInCaseBlobs(itemKey, folderName, pageName)
    await loadFiles()
  } catch (error) {
    console.error('[handleDetailAddFolder]', error)
    setFileError('Unable to create folder. Please try again.')
  } finally {
    setIsFilesLoading(false)
  }
}
export const editAttachments = async (
  itemKey: string,
  files: any[],
  pageName?: string,
  folderName?: string
): Promise<void> => {
  const fileStructure = await getFilesForDetail(itemKey, pageName)

  const existingFiles = fileStructure?.allFilesList || []

  const retainedFileNames: string[] = []
  const uploadFiles: File[] = []

  for (const file of files) {
    // existing file already loaded
    if (file?.Name) {
      retainedFileNames.push(file.Name)
    }

    // wrapped File object
    else if (file?.file instanceof File) {
      uploadFiles.push(file.file)
    }

    // raw File object
    else if (file instanceof File) {
      uploadFiles.push(file)
    }
  }

  const filesToDelete = existingFiles.filter(
    (existing: any) => !retainedFileNames.includes(existing.Name)
  )

  await Promise.all(
    filesToDelete.map((file: any) =>
      deleteDetailFile(
        itemKey,
        folderName ? { ...file, folderName } : file,
        pageName
      )
    )
  )

  if (uploadFiles.length > 0) {
    await uploadDetailFiles(
      itemKey,
      uploadFiles,
      folderName,
      undefined,
      pageName
    )
  }
}
export const handleDetailUpdateFolderName = (
  oldName: string,
  newName: string,
  setFolders: (updater: (current: any[]) => any[]) => void
): void => {
  setFolders((current) =>
    current.map((folder: any) => (folder.title === oldName ? { ...folder, title: newName } : folder))
  )
}